/**
 * Analytics Page - Owner Portal
 *
 * Purpose:
 *   Renders the Owner Analytics view: KPIs (period revenue, reservations), Revenue and
 *   Reservations charts by channel, and Highcharts-based trend charts (monthly revenue,
 *   occupancy, ADR, lead time, LOS, day-of-week heatmap, cancellations, inquiries,
 *   revenue mix). Data is fetched from GET /api/v1/owner/analytics, scoped by
 *   selectedProperty.listing_id and ReportingPeriod range.
 *
 * Workflow:
 *   Session (selectedProperty) + ReportingPeriod (rangeStart, rangeEnd) -> fetch analytics
 *   -> sanitize with effectiveMode -> destructure and render charts/header -> channel
 *   filter updates (selectedChannels) trigger refetch when single channel selected.
 *
 * Assumptions / Invariants:
 *   - Owner-only endpoint usage; no admin APIs or admin-only imports.
 *   - sanitizeForOwnerView(dashboard, effectiveMode) is always applied before
 *     destructuring or using dashboard data in UI.
 *   - listingId must be a positive integer (or null); guards reject NaN, <=0, missing.
 *   - rangeStart / rangeEnd are Date instances from context; identity can churn
 *     when preset or custom range changes, which may trigger redundant refetches.
 *
 * Dependencies:
 *   - react (useState, useEffect, useMemo, useCallback)
 *   - ownerApi.getOwnerAnalytics
 *   - owner components: RevenueChart, ReservationsChart, AnalyticsHeader, OwnerPageChrome,
 *     analytics/* (IconsRow, MonthlyRevenueTrendChart, OccupancyTrendChart, ADRTrendChart, etc.)
 *   - utils: sanitizeForOwnerView
 *   - contexts: useSession, useReportingPeriod
 *   - shared: SkeletonCard
 *
 * Version: 1.1.0
 *
 * Changelog:
 *   - 1.1.0 (Phase 0): Charter header, intent comments. No behavior changes.
 *   - 1.0.0: Initial Analytics page (KPIs, Revenue/Reservations, Highcharts trends).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getOwnerAnalytics } from '../../services/ownerApi.js';
import RevenueChart from '../../components/owner/RevenueChart.jsx';
import ReservationsChart from '../../components/owner/ReservationsChart.jsx';
import MonthlyRevenueTrendChart from '../../components/owner/analytics/MonthlyRevenueTrendChart.jsx';
import OccupancyTrendChart from '../../components/owner/analytics/OccupancyTrendChart.jsx';
import ADRTrendChart from '../../components/owner/analytics/ADRTrendChart.jsx';
import LeadTimeDistributionChart from '../../components/owner/analytics/LeadTimeDistributionChart.jsx';
import LengthOfStayDistributionChart from '../../components/owner/analytics/LengthOfStayDistributionChart.jsx';
import DayOfWeekHeatmapChart from '../../components/owner/analytics/DayOfWeekHeatmapChart.jsx';
import RevenueMixChart from '../../components/owner/analytics/RevenueMixChart.jsx';
import CancellationsTrendChart from '../../components/owner/analytics/CancellationsTrendChart.jsx';
import AnalyticsHeader from '../../components/owner/analytics/AnalyticsHeader.jsx';
import AnalyticsFiltersSheet from '../../components/owner/analytics/AnalyticsFiltersSheet.jsx';
import GuideModal from '../../components/owner/analytics/GuideModal.jsx';
import HighchartsChartWrapper from '../../components/owner/analytics/HighchartsChartWrapper.jsx';
import IconsRow from '../../components/owner/analytics/IconsRow.jsx';
import LazyChartSection from '../../components/owner/analytics/LazyChartSection.jsx';
import '../../components/owner/analytics/highchartsDefaults.js'; // credits disabled for all analytics charts
import { sanitizeForOwnerView } from '../../utils/sanitizeForOwnerView.js';
import { useSession } from '../../contexts/SessionContext.jsx';
import { useReportingPeriod } from '../../contexts/ReportingPeriodContext.jsx';
import { useViewport } from '../../contexts/ViewportContext.jsx';
import { SkeletonCard } from '../../components/shared/SkeletonLoader.jsx';
import OwnerPageChrome from '../../components/owner/OwnerPageChrome.jsx';

export default function AnalyticsPage({ onLogout }) {
  const { effectiveMode, selectedProperty } = useSession();
  const { rangeStart, rangeEnd, getResolvedSpanText } = useReportingPeriod();
  const { isMobile } = useViewport();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  // CRITICAL: These hooks must be BEFORE any conditional early returns (React Rules of Hooks)
  const [showGuideModal, setShowGuideModal] = useState(false);

  // Extract listing_id from selectedProperty (canonical property key).
  // Guards: reject null/undefined, NaN, <=0 so listingId is either positive integer or null.
  // Invariant: API requires positive integer; no fetch when listingId is null.
  const listingId = useMemo(() => {
    const id = selectedProperty?.listing_id;
    if (!id) return null;
    const numId = typeof id === 'string' ? parseInt(id, 10) : id;
    return isNaN(numId) || numId <= 0 ? null : numId;
  }, [selectedProperty?.listing_id]);

  // Fetch analytics when listingId, rangeStart, rangeEnd, or selectedChannels change.
  // Dates are used to build YYYY-MM-DD strings for the API; rangeStart/rangeEnd come from
  // ReportingPeriodContext and can change identity (new Date refs) -> possible redundant
  // refetches. Phase 1: Added AbortController and mount guard to prevent setState-after-unmount.
  // Phase 5: Use stable dependency keys (YYYY-MM-DD strings) instead of Date object references
  // to prevent redundant refetches when logical range is unchanged but Date identity churns.
  const rangeStartKey = rangeStart ? `${rangeStart.getFullYear()}-${String(rangeStart.getMonth() + 1).padStart(2, '0')}-${String(rangeStart.getDate()).padStart(2, '0')}` : '';
  const rangeEndKey = rangeEnd ? `${rangeEnd.getFullYear()}-${String(rangeEnd.getMonth() + 1).padStart(2, '0')}-${String(rangeEnd.getDate()).padStart(2, '0')}` : '';
  
  // Debug instrumentation (gated by ?debug_analytics=1 query param)
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const debugAnalytics = urlParams?.get('debug_analytics') === '1';
  
  useEffect(() => {
    if (debugAnalytics) {
      console.log('[AnalyticsPage] Effect triggered', {
        listingId,
        rangeStartKey,
        rangeEndKey,
        selectedChannels: selectedChannels.length,
        timestamp: new Date().toISOString(),
      });
    }
    // AbortController cancels in-flight fetch on unmount or dependency change
    const abortController = new AbortController();
    const signal = abortController.signal;
    
    // Mount guard: prevents setState after component unmounts or effect re-runs
    let isActive = true;
    
    const fetchAnalytics = async () => {
      // Don't fetch if no property selected
      if (!listingId) {
        if (!isActive || signal.aborted) return;
        setLoading(false);
        setError('No property selected');
        return;
      }
      
      if (!isActive || signal.aborted) return;
      setLoading(true);
      setError(null);
      
      try {
        // Format dates as ISO date strings (YYYY-MM-DD)
        // Use local date components to avoid timezone shifts
        // Note: rangeStart/rangeEnd are original Date objects (not keys) - used for formatting
        const startDateStr = `${rangeStart.getFullYear()}-${String(rangeStart.getMonth() + 1).padStart(2, '0')}-${String(rangeStart.getDate()).padStart(2, '0')}`;
        const endDateStr = `${rangeEnd.getFullYear()}-${String(rangeEnd.getMonth() + 1).padStart(2, '0')}-${String(rangeEnd.getDate()).padStart(2, '0')}`;
        
        // Pass channel param when single channel selected (backend filters)
        // When "All" is selected (selectedChannels.length !== 1), omit channel param
        const channelParam = selectedChannels.length === 1 
          ? selectedChannels[0] 
          : undefined;
        
        if (debugAnalytics) {
          console.log('[AnalyticsPage] Fetch starting', {
            listingId,
            startDateStr,
            endDateStr,
            channelParam,
            timestamp: new Date().toISOString(),
          });
        }
        
        const response = await getOwnerAnalytics({
          listingId: listingId,
          startDate: startDateStr,
          endDate: endDateStr,
          channel: channelParam,
          metrics: 'revenue,reservations,kpis,monthlyTrend,occupancyTrend,adrTrend,cancellationsTrend,leadTimeDist,losDist,dowHeatmap,comparison,revenueMix',
          signal,
        });
        
        // Guard: check if component unmounted or request aborted before setState
        if (!isActive || signal.aborted) return;
        
        // Response structure from sendOwnerSuccess: { outcome, message, ...data }
        // Analytics data is spread at top level
        // Backend now returns masterChannels (4 master channels) and channels (backward compatibility alias)
        const analyticsData = {
          revenue_by_channel: response.revenue_by_channel || [],
          reservations_by_channel: response.reservations_by_channel || [],
          channels: response.masterChannels || response.channels || [], // Prefer masterChannels, fallback to channels
          masterChannels: response.masterChannels || response.channels || [],
          appliedFilters: response.appliedFilters || {},
          chartAnnotations: response.chartAnnotations || [],
          kpis: response.kpis || {},
          dateRange: response.dateRange || { start: startDateStr, end: endDateStr },
          analyticsV2: response.analyticsV2 || null, // Phase 2: New Highcharts data blocks
          analyticsV3: response.analyticsV3 || null, // Phase 3: Insights, comparison, revenue mix
        };
        
        if (!isActive || signal.aborted) return;
        setDashboard(analyticsData);
      } catch (err) {
        // AbortError: request was cancelled (unmount or dependency change) - handle silently
        if (err.name === 'AbortError' || signal.aborted) {
          return; // Silent: no user-visible error, no console.error
        }
        
        // Other errors: only setError if component is still mounted
        if (!isActive || signal.aborted) return;
        console.error('[AnalyticsPage] Error fetching analytics:', err);
        setError(err.message || 'Failed to load analytics data');
      } finally {
        // Only update loading state if component is still mounted and not aborted
        if (isActive && !signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchAnalytics();
    
    // Cleanup: abort in-flight request and mark as inactive
    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [listingId, rangeStartKey, rangeEndKey, selectedChannels]);

  // Phase 3: stable reference to avoid prop churn for AnalyticsHeader
  const handleChannelsChange = useCallback((channels) => {
    setSelectedChannels(channels);
  }, []);

  // Memoize derived values (computed before early returns for Rules of Hooks)
  const sanitizedDashboard = useMemo(
    () => sanitizeForOwnerView(dashboard, effectiveMode),
    [dashboard, effectiveMode]
  );
  const dateRangeDisplay = useMemo(
    () => getResolvedSpanText(),
    [getResolvedSpanText]
  );

  // Count Phase 2 + Revenue Mix chart cards so we can span last child when odd (no blank right column).
  // MUST be before any early return to satisfy Rules of Hooks (same hook count every render).
  const phase2ChartCount = useMemo(() => {
    const v2 = sanitizedDashboard?.analyticsV2;
    const v3 = sanitizedDashboard?.analyticsV3;
    let n = 0;
    if (v2) {
      n += [
        v2.monthlyTrend,
        v2.occupancyTrend,
        v2.adrTrend,
        v2.leadTimeDist,
        v2.losDist,
        v2.dowHeatmap,
        v2.cancellationsTrend,
      ].filter(Boolean).length;
    }
    if (v3?.revenueMix) n += 1;
    return n;
  }, [sanitizedDashboard?.analyticsV2, sanitizedDashboard?.analyticsV3]);

  if (loading) {
    return (
      <OwnerPageChrome title="Property" subtitle="Loading..." compactLayout={isMobile}>
        <SkeletonCard />
      </OwnerPageChrome>
    );
  }

  if (error || !dashboard) {
    return (
      <OwnerPageChrome title="Property" subtitle="Error" compactLayout={isMobile}>
        <div className="owner-alert owner-alert-error">
          {error || 'Failed to load analytics data'}
        </div>
      </OwnerPageChrome>
    );
  }

  // Defense-in-depth: sanitizedDashboard from useMemo above (dashboard, effectiveMode)
  const { revenue_by_channel = [], reservations_by_channel = [], channels = [], chartAnnotations = [], kpis: backendKPIs = {}, analyticsV2 = null, analyticsV3 = null } = sanitizedDashboard;
  
  // Get selected channel for filtering new charts
  const selectedChannel = selectedChannels.length === 1 ? selectedChannels[0] : null;
  
  // dateRangeDisplay from useMemo above (getResolvedSpanText)
  
  // Prepare KPIs for header - safe defaults from backend (source of truth)
  const periodRevenue = Number(backendKPIs?.period_revenue ?? 0);
  const periodReservations = Number(backendKPIs?.period_reservations ?? 0);

  // Get property name for header
  const propertyName = selectedProperty?.clc_short_name || selectedProperty?.display_name || selectedProperty?.name || 'Property';

  // Backend handles channel filtering - use data as-is
  // Charts will display what backend returns (already filtered if channel param was provided)

  // Mobile: single "Filters" button + date range tap open sheet. Desktop: pills in header + full AnalyticsHeader.
  const headerTopRightSlot = isMobile ? (
    <div style={styles.headerTopRightComposite}>
      <button
        type="button"
        onClick={() => setFiltersSheetOpen(true)}
        style={styles.filtersButton}
        aria-label="Open filters"
      >
        Filters
      </button>
      <IconsRow onOpenGuide={() => setShowGuideModal(true)} />
    </div>
  ) : (
    <div style={styles.headerTopRightComposite}>
      <IconsRow onOpenGuide={() => setShowGuideModal(true)} />
    </div>
  );

  const headerRightSlot = isMobile ? (
    <button
      type="button"
      onClick={() => setFiltersSheetOpen(true)}
      style={styles.dateRangeButton}
      aria-label="Change reporting period and filters"
    >
      {dateRangeDisplay || 'Select period'}
    </button>
  ) : (
    <AnalyticsHeader
      propertyName={propertyName}
      dateRangeDisplay={dateRangeDisplay}
      channels={channels}
      selectedChannels={selectedChannels}
      onChannelsChange={handleChannelsChange}
      comparison={analyticsV3?.comparison}
      selectedChannel={selectedChannel}
      hideChannelFilter={false}
    />
  );

  const chartsGridStyle = isMobile
    ? { ...styles.chartsGrid, gridTemplateColumns: '1fr', gap: 'var(--spacing-4)', marginBottom: 'var(--spacing-4)' }
    : styles.chartsGrid;

  return (
    <OwnerPageChrome
      title={propertyName}
      subtitle={`Analytics: Revenue and reservation trends`}
      headerTopRightSlot={headerTopRightSlot}
      headerRightSlot={headerRightSlot}
      compactLayout={isMobile}
    >
      <div data-page="analytics" style={styles.analyticsPageWrap}>
        <style>{`.analytics-charts-grid-odd > *:last-child { grid-column: 1 / -1; }`}</style>
        {/* Charts - single column on mobile, two columns on desktop */}
        <div style={chartsGridStyle}>
          <RevenueChart 
            data={revenue_by_channel} 
            channels={channels}
            onSegmentClick={() => {}}
            selectedChannels={selectedChannels}
            annotations={chartAnnotations}
            periodRevenue={periodRevenue}
            isMobile={isMobile}
          />
          <ReservationsChart 
            data={reservations_by_channel} 
            channels={channels}
            onSegmentClick={() => {}}
            selectedChannels={selectedChannels}
            annotations={chartAnnotations}
            periodReservations={periodReservations}
            isMobile={isMobile}
          />
        </div>

        {/* Phase 2 + Revenue Mix: lazy-loaded on mobile, single column on mobile, two on desktop */}
        {(analyticsV2 || (analyticsV3?.revenueMix)) && (
          <LazyChartSection isMobile={isMobile}>
            <div
              style={chartsGridStyle}
              className={phase2ChartCount % 2 === 1 && !isMobile ? 'analytics-charts-grid-odd' : undefined}
              data-chart-count={debugAnalytics ? phase2ChartCount : undefined}
            >
          {/* Monthly Revenue Trend */}
          {analyticsV2?.monthlyTrend && (
            <HighchartsChartWrapper
              title="Monthly Revenue Trend"
              definition="Total revenue by month. When 'All' is selected you see every channel; when you filter, you see that channel only."
              limitations="Revenue is attributed to the month of check-in. Use it to track income over time and compare channels. We count confirmed stays and owner stays; we don't count inquiries or cancelled reservations."
              futureMonthsNote
            >
              <MonthlyRevenueTrendChart 
                data={analyticsV2.monthlyTrend}
                masterChannels={channels}
                selectedChannel={selectedChannel}
              />
            </HighchartsChartWrapper>
          )}
          
          {/* Occupancy Trend */}
          {analyticsV2?.occupancyTrend && (
            <HighchartsChartWrapper
              title="Monthly Occupancy Rate"
              definition="Share of available nights that were booked, per month. Shown as a percentage."
              limitations="Can exceed 100% if you have overlapping stays or multiple units. Use it to see how full your calendar is. Based on booked nights and calendar days. We count confirmed and owner stays only; inquiries are not included."
              futureMonthsNote
            >
              <OccupancyTrendChart data={analyticsV2.occupancyTrend} />
            </HighchartsChartWrapper>
          )}
          
          {/* ADR Trend */}
          {analyticsV2?.adrTrend && (
            <HighchartsChartWrapper
              title="Average Daily Rate (ADR) Trend"
              definition="Average revenue per night, by month. Total revenue divided by total nights."
              limitations="Use it to see whether your nightly rate is rising or falling over time. From confirmed reservations only; excludes cancelled."
              futureMonthsNote
            >
              <ADRTrendChart data={analyticsV2.adrTrend} />
            </HighchartsChartWrapper>
          )}
          
          {/* Lead Time Distribution */}
          {analyticsV2?.leadTimeDist && (
            <HighchartsChartWrapper
              title="Lead Time Distribution"
              definition="How far in advance guests booked their stay. Each bar is a range of days before check-in (e.g. 15–30 days)."
              limitations="Short lead times mean last-minute bookings and can mean more volatility. Use it to plan staffing and cleaning. Based on booking date vs check-in."
              futureMonthsNote
            >
              <LeadTimeDistributionChart data={analyticsV2.leadTimeDist} />
            </HighchartsChartWrapper>
          )}
          
          {/* Length of Stay Distribution */}
          {analyticsV2?.losDist && (
            <HighchartsChartWrapper
              title="Length of Stay Distribution"
              definition="How many nights guests stay. Each bar is a length (e.g. 1 night, 4–6 nights)."
              limitations="Each reservation counts once. Use it to see typical stay lengths and plan turnovers. Counts by check-in month."
              futureMonthsNote
            >
              <LengthOfStayDistributionChart data={analyticsV2.losDist} />
            </HighchartsChartWrapper>
          )}
          
          {/* Check-in Day of Week Heatmap */}
          {analyticsV2?.dowHeatmap && (
            <HighchartsChartWrapper
              title="Check-in Day of Week Heatmap"
              definition="Which days of the week guests check in, by month. Each square is a day-of-week and month; darker means more arrivals."
              limitations="Use it to anticipate busy arrival days and plan turnovers. Based on check-in date, not booking date."
              futureMonthsNote
            >
              <DayOfWeekHeatmapChart data={analyticsV2.dowHeatmap} />
            </HighchartsChartWrapper>
          )}

          {/* Monthly Cancellations (left column when paired with Revenue Mix) */}
          {analyticsV2?.cancellationsTrend && (
            <HighchartsChartWrapper
              title="Monthly Cancellations"
              definition="Number of cancelled reservations by check-in month. Shows when cancellations happen over time."
              limitations="Includes all cancellations in the period. Use it to spot patterns or problem months. Counts by check-in month of the cancelled stay."
              futureMonthsNote
            >
              <CancellationsTrendChart data={analyticsV2.cancellationsTrend} />
            </HighchartsChartWrapper>
          )}

          {/* Revenue Mix Over Time (right column beside Monthly Cancellations) */}
          {analyticsV3?.revenueMix && (
            <HighchartsChartWrapper
              title="Revenue Mix Over Time"
              definition="Each channel's share of total revenue over time (stacked to 100%). Shows proportion, not dollar amounts."
              limitations="A channel's share can go up even if its revenue goes down, if others drop more. Use it to see which channels dominate."
              futureMonthsNote
            >
              <RevenueMixChart
                data={analyticsV3.revenueMix}
                masterChannels={channels}
              />
            </HighchartsChartWrapper>
          )}
            </div>
          </LazyChartSection>
        )}
      </div>

      {/* Modals */}
      {showGuideModal && (
        <GuideModal onClose={() => setShowGuideModal(false)} />
      )}
      {isMobile && (
        <AnalyticsFiltersSheet
          isOpen={filtersSheetOpen}
          onClose={() => setFiltersSheetOpen(false)}
          channels={channels}
          selectedChannels={selectedChannels}
          onChannelsChange={handleChannelsChange}
        />
      )}
    </OwnerPageChrome>
  );
}

const styles = {
  headerTopRightComposite: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
    flexWrap: 'wrap',
    minWidth: 0,
  },
  pillsWrap: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    minWidth: 0,
    maxWidth: '100%',
  },
  pillsDivider: {
    width: '1px',
    height: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
    flexShrink: 0,
  },
  filtersButton: {
    padding: '8px 14px',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px',
  },
  dateRangeButton: {
    padding: '8px 12px',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-primary)',
    backgroundColor: 'transparent',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    textAlign: 'left',
    minHeight: '44px',
    width: '100%',
    maxWidth: '100%',
  },
  analyticsPageWrap: {
    width: '100%',
    minWidth: 0,
  },
  container: {
    padding: 'var(--spacing-6)',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: 'var(--spacing-6)',
  },
  title: {
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 'var(--font-weight-bold)',
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 'var(--spacing-6)',
    marginBottom: 'var(--spacing-6)',
  },
  newChartsSection: {
    marginTop: 'var(--spacing-8)',
    paddingTop: 'var(--spacing-6)',
    borderTop: '1px solid var(--color-border-light, #e5e7eb)',
  },
  sectionTitle: {
    fontSize: 'var(--font-size-xl)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    marginBottom: 'var(--spacing-6)',
  },
  chartCard: {
    backgroundColor: 'var(--owner-card-bg)',
    borderRadius: 'var(--owner-card-radius)',
    padding: 'var(--spacing-4)',
    boxShadow: 'var(--owner-card-shadow)',
    minHeight: '400px',
  },
};

